import { Link } from "wouter";
import { useBookCta } from "@/hooks/use-book-href";
import { KUALOA_STAY_PATH } from "@shared/property-context";
import { useQuery } from "@tanstack/react-query";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";
import { StickyMobileBookCta } from "@/components/layout/sticky-mobile-book-cta";
import { Breadcrumbs } from "@/components/layout/breadcrumbs";
import { PageHero } from "@/components/layout/page-hero";
import { LocationMap } from "@/components/property/location-map";
import { SectionHeading } from "@/components/ui/section-heading";
import {
  useSEO,
  useSEOSettings,
  DEFAULT_SEO_SETTINGS,
  generateBreadcrumbSchema,
  generateFAQSchema,
  generateKnowledgeGraphSchema,
  generateSpeakableSchema,
  type SiteInfoSettings,
} from "@/lib/seo-utils";
import { attractionSlug } from "@shared/slug-utils";
import { wavesVideo, parallaxBg, beachShoreline, wavesPoster } from "@/lib/media-assets";
import { LazyVideo } from "@/components/ui/lazy-video";
import { optimizedUrl, optimizedSrcSet } from "@/lib/img";
import { ScrollReveal } from "@/components/ui/scroll-reveal";
import type { NearbyAttraction } from "@shared/schema";

const areaFaqs = [
  {
    question: "Where is Ka'a'awa on Oahu?",
    answer:
      "Ka'a'awa is a small coastal community on Oahu's windward (eastern) shore between Kaneohe and Laie, known for dramatic mountain-meets-ocean scenery and a relaxed pace far from Waikiki crowds.",
  },
  {
    question: "Why stay in Ka'a'awa instead of Honolulu?",
    answer:
      "Ka'a'awa offers a quieter, nature-focused base with direct beach access, Kualoa Ranch adventures across the street, and easy drives to North Shore surf towns and the Polynesian Cultural Center.",
  },
  {
    question: "How far is Ka'a'awa from the airport?",
    answer:
      "Honolulu International Airport (HNL) is about 23 miles away, roughly a 30-minute drive via the Pali Highway or H-3, depending on traffic.",
  },
];

export default function KaaawaPage() {
  const bookCta = useBookCta();
  const { seoSettings } = useSEOSettings();
  const { data: siteSettings } = useQuery<SiteInfoSettings>({
    queryKey: ["/api/settings/site_info"],
    retry: false,
  });
  const { data: attractions = [] } = useQuery<NearbyAttraction[]>({
    queryKey: ["/api/nearby-attractions"],
  });

  const seoHead = useSEO({
    pageType: "kaaawa",
    title: DEFAULT_SEO_SETTINGS.page_titles.kaaawa,
    description: DEFAULT_SEO_SETTINGS.meta_descriptions.kaaawa,
    canonical: "https://vacationrentaloahu.co/kaaawa",
    structuredData: [
      generateKnowledgeGraphSchema({ seoSettings, siteInfoSettings: siteSettings }),
      generateBreadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Ka'a'awa Area Guide", url: "/kaaawa" },
      ]),
      generateFAQSchema(areaFaqs),
      generateSpeakableSchema(["h1", ".faq-answer"]),
      {
        "@context": "https://schema.org",
        "@type": "TouristDestination",
        name: "Ka'a'awa, Oahu",
        description: "Windward coast destination for beachfront stays near Kualoa Ranch and Mokoli'i (Chinaman's Hat).",
        url: "https://vacationrentaloahu.co/kaaawa",
        containedInPlace: {
          "@type": "Place",
          name: "Oahu, Hawaii",
        },
      },
    ],
  });

  return (
    <>
      {seoHead}
      <div className="min-h-screen bg-shell pb-24 lg:pb-0 selection:bg-sun selection:text-shell">
        <Header />
        <main id="main-content">
          <PageHero
            eyebrow="Windward Coast"
            title="Ka'a'awa, Oahu"
            subtitle="Your windward coast home base. Beachfront stays with Kualoa Ranch across the street and Mokoli'i (Chinaman's Hat) visible from the lanai."
            videoSrc={wavesVideo}
            posterSrc={wavesPoster}
            imageAlt="Scenic Ka'a'awa coastline on Oahu's windward shore near Kualoa Ranch"
          />

          <section className="py-16 md:py-24 bg-shell">
            <div className="container mx-auto px-6 max-w-3xl">
              <Breadcrumbs
                items={[
                  { name: "Home", url: "/" },
                  { name: "Ka'a'awa Guide", url: "/kaaawa" },
                ]}
                className="mb-12 justify-center"
              />

              <ScrollReveal>
                <p className="text-luxury-xl text-ink font-serif leading-relaxed mb-16 text-center">
                  Ka&apos;a&apos;awa sits on one of Oahu&apos;s most scenic stretches of coastline, where the Koolau mountains
                  drop straight into the Pacific. Our beachfront vacation rental puts you in the heart of it.
                </p>
              </ScrollReveal>

              <SectionHeading
                eyebrow="The Area"
                title="What makes Ka'a'awa special"
                align="left"
              />
              <div className="space-y-6 text-ink-soft leading-relaxed text-lg">
                <p>
                  Unlike busy resort zones, Ka&apos;a&apos;awa retains authentic local character. Mornings start with ocean views and
                  calm water; afternoons might mean a Kualoa Ranch ATV tour, a kayak paddle to Mokoli&apos;i, or a drive up the coast
                  to Haleiwa for shave ice on the North Shore.
                </p>
                <p>
                  Our property is one of the few true oceanfront vacation rentals in the area, making Ka&apos;a&apos;awa the ideal choice
                  for families who want beach access without sacrificing space, privacy, or a full kitchen.
                </p>
              </div>
            </div>
          </section>

          <section className="scenic-editorial relative overflow-hidden" aria-label="Ka'a'awa beach atmosphere">
            <div className="absolute inset-0 z-0" aria-hidden="true">
              <LazyVideo
                src={wavesVideo}
                poster={wavesPoster}
                autoPlay
                loop
                muted
                playsInline
                className="absolute inset-0 h-full w-full object-cover"
              />

            </div>
            <div className="relative z-10 container mx-auto px-6 max-w-2xl text-center py-6 media-copy-panel">
              <p className="wordmark-gold text-[11px] font-sans uppercase tracking-[0.3em] mb-3">Beachfront Living</p>
              <p className="wordmark-gold font-serif text-[clamp(24px,4.5vw,36px)] leading-tight tracking-tight">
                Wake up to mountain-meets-ocean views and private beach access steps from your lanai.
              </p>
            </div>
          </section>

          {attractions.length > 0 && (
            <section className="py-20 bg-shell border-y border-sand-deep">
              <div className="container mx-auto px-6 max-w-5xl">
                <SectionHeading 
                  eyebrow="Local Spots"
                  title="Explore nearby"
                />
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                  {attractions.slice(0, 8).map((a) => (
                    <Link
                      key={a.id}
                      href={`/nearby/${attractionSlug(a.name)}`}
                      className="group flex flex-col overflow-hidden bg-sand border border-sand-deep transition-transform duration-300 hover:-translate-y-1"
                      data-testid={`nearby-card-${attractionSlug(a.name)}`}
                    >
                      <div className="relative aspect-[4/3] w-full overflow-hidden bg-sand-deep">
                        {a.image_url ? (
                          <img
                            src={optimizedUrl(a.image_url, 640)}
                            srcSet={optimizedSrcSet(a.image_url, [400, 640, 900])}
                            sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
                            alt={a.name}
                            className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <img src={optimizedUrl(wavesPoster, 640)} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" decoding="async" />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <span className="text-luxury-lg font-serif leading-tight text-ink transition-colors group-hover:text-palm">{a.name}</span>
                        {a.distance && (
                          <p className="mt-2 text-driftwood text-[11px] uppercase tracking-widest font-semibold">{a.distance}</p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <section className="py-20 md:py-28 bg-shell">
            <div className="container mx-auto px-6 max-w-5xl">
              <SectionHeading 
                eyebrow="Location"
                title="Find us on the map"
              />
              {/* No overflow clipping here: it would stop the map from sticking as the list scrolls. */}
              <div className="mt-12 rounded-2xl border border-sand-deep">
                <LocationMap />
              </div>
            </div>
          </section>

          <section className="py-20 bg-shell border-t border-sand-deep">
            <div className="container mx-auto px-6 max-w-3xl">
              <SectionHeading
                eyebrow="FAQ"
                title="Area Questions"
              />
              <div className="space-y-0 mt-12 border-t border-sand-deep">
                {areaFaqs.map((faq, idx) => (
                  <div key={faq.question} className="py-8 border-b border-sand-deep group">
                    <h2 className="text-luxury-xl font-serif text-forest mb-4 tracking-luxury-tight group-hover:text-palm transition-colors duration-300 flex gap-4">
                      <span className="text-driftwood font-sans font-light italic text-base mt-1">{String(idx + 1).padStart(2, '0')}</span>
                      {faq.question}
                    </h2>
                    <p className="text-ink-soft leading-relaxed pl-9">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-20 md:py-28 bg-sand border-t border-sand-deep">
            <div className="container mx-auto px-6 text-center max-w-2xl">
              <SectionHeading
                eyebrow="Plan Your Trip"
                title="Ready to explore?"
                lede="Book your stay at our Ka'a'awa beach house and wake up to the windward coast every morning."
              />
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                <Link href={bookCta.href} className="inline-flex items-center justify-center bg-sun text-shell hover:bg-sun-hover font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 rounded-lg transition-transform duration-300 hover:-translate-y-0.5">
                  {bookCta.choosing ? "Choose a Home" : "Book Direct"}
                </Link>
                <Link href={KUALOA_STAY_PATH} className="inline-flex items-center justify-center bg-transparent border border-ink text-ink hover:bg-ink hover:text-shell font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 rounded-lg transition-all duration-300">
                  View Our Beach House
                </Link>
                <Link href="/blog" className="inline-flex items-center justify-center text-ink hover:text-ink-soft hover:underline font-sans font-semibold uppercase tracking-[0.08em] px-8 py-4 transition-colors duration-300">
                  Oahu Travel Guide
                </Link>
              </div>
            </div>
          </section>
        </main>
        <StickyMobileBookCta />
      <Footer />
      </div>
    </>
  );
}
